import turfCircle from "@turf/circle";
import bearing from "@turf/bearing";
import destination from "@turf/destination";
import distance from "@turf/distance";
import { MAX_CIRCLE_RADIUS_METERS } from "@iventis/utilities";
import GeoJSON from "geojson";
import { FixedShapeSupportedGeometry } from "../types/internal";
import { getCoordinateList, numbersEqualWithin, updateCoordinates } from "./geojson-helpers";

const CIRCLE_STEPS = 36;

const clampCircleRadius = (radius: number) => Math.min(radius, MAX_CIRCLE_RADIUS_METERS);

/** Creates circle coordinates from a center and a point on the circumference, always producing 37 coordinates (36 + closing). */
export function createCircleFromCenterAndRadius(center: GeoJSON.Position, radius: number): GeoJSON.Position[] {
    const circleFeature = turfCircle(center, radius, { steps: CIRCLE_STEPS, units: "meters" });
    return circleFeature.geometry.coordinates[0];
}

/** Gets the center of a circle geometry from the geodesic midpoint of two opposite perimeter coordinates. */
export function getCircleCenter(geometry: FixedShapeSupportedGeometry): GeoJSON.Position {
    const coords = getCoordinateList(geometry);
    const uniqueCoords = coords.length > 1 && coords[0][0] === coords[coords.length - 1][0] && coords[0][1] === coords[coords.length - 1][1] ? coords.slice(0, -1) : coords;

    if (uniqueCoords.length <= 1) {
        return uniqueCoords[0] || [0, 0];
    }

    // Turf circles have evenly spaced perimeter points. Two points halfway
    // around the coordinate list form a diameter, whose geodesic midpoint is
    // the original center. Averaging longitude/latitude introduces an error
    // that grows with the radius (and fails for circles crossing the dateline).
    const firstCoordinate = uniqueCoords[0];
    const oppositeCoordinate = uniqueCoords[Math.floor(uniqueCoords.length / 2)];
    const diameter = distance(firstCoordinate, oppositeCoordinate, { units: "meters" });
    return destination(firstCoordinate, diameter / 2, bearing(firstCoordinate, oppositeCoordinate), { units: "meters" }).geometry.coordinates;
}

/** Gets the radius of a circle geometry in meters */
export function getCircleRadius(geometry: FixedShapeSupportedGeometry): number {
    const center = getCircleCenter(geometry);
    const coords = getCoordinateList(geometry);
    if (coords.length < 2) return 0;
    return distance(center, coords[0], { units: "meters" });
}

/** Resizes a circle during drawing - determines the radius from center to target coordinate */
export function resizeCircle(currentGeom: FixedShapeSupportedGeometry, targetCoord: GeoJSON.Position): FixedShapeSupportedGeometry {
    const listOfCoordinates = getCoordinateList(currentGeom);
    if (currentGeom.coordinates.length === 0 || listOfCoordinates.every((coord) => numbersEqualWithin(targetCoord, coord, 0))) {
        return {
            ...currentGeom,
            coordinates: [currentGeom.type === "Polygon" ? [targetCoord, targetCoord] : targetCoord],
        } as FixedShapeSupportedGeometry;
    }

    // The center is the first coordinate placed
    const center = getCircleCenter(currentGeom);
    const radius = clampCircleRadius(distance(center, targetCoord, { units: "meters" }));

    const coords = createCircleFromCenterAndRadius(center, radius);
    return updateCoordinates(currentGeom, coords) as FixedShapeSupportedGeometry;
}

/** Resizes a circle by setting a new radius value (used in object details) */
export function resizeCircleByRadius(geometry: FixedShapeSupportedGeometry, newRadius: number): FixedShapeSupportedGeometry {
    const center = getCircleCenter(geometry);
    const coords = createCircleFromCenterAndRadius(center, clampCircleRadius(newRadius));
    return updateCoordinates(geometry, coords);
}

/** Resizes a circle when a node is dragged - adjusts radius based on new node position */
export function resizeCircleFromNodeDrag(geometry: FixedShapeSupportedGeometry, newCoordinate: GeoJSON.Position): FixedShapeSupportedGeometry {
    const center = getCircleCenter(geometry);
    const newRadius = clampCircleRadius(distance(center, newCoordinate, { units: "meters" }));
    const coords = createCircleFromCenterAndRadius(center, newRadius);
    return updateCoordinates(geometry, coords);
}
